import { useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Asset, type Board } from '@air/api/types';
import { Modal, ModalDescription } from '@air/primitive-modal';

import { RegenerateShareLinkButtons } from '~/components/SharePrivateContentModal/RegenerateLink/RegenerateShareLinkButtons';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  type AssetScopedShareLinkShortIdInfo,
  useRegenerateAssetScopedShortId,
} from '~/swr-hooks/shortUrl/useRegenerateAssetScopedShortId';

export type SharePrivateClipRegenerateProps = {
  assetId: Asset['id'];
  trackLocation: AirActionTrackingLocation;
  shortIdInfo: AssetScopedShareLinkShortIdInfo;
  boardContext?: Board['id'];
  onRegenerated: () => void;
  onCancel: () => void;
};

export const SharePrivateClipRegenerate = ({
  assetId,
  trackLocation,
  shortIdInfo,
  boardContext,
  onClose,
  onCancel,
  onRegenerated,
}: AirModalProps<SharePrivateClipRegenerateProps>) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { regenerateAssetScopedShortId } = useRegenerateAssetScopedShortId();

  const onRegenerate = useCallback(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      throw new Error('No workspace id');
    }

    regenerateAssetScopedShortId.mutate(
      {
        workspaceId,
        assetId,
        shortIdInfo,
        boardContext,
        trackLocation,
      },
      {
        onSuccess: () => {
          onClose();
          onRegenerated();
        },
      },
    );
  }, [
    assetId,
    boardContext,
    currentWorkspace?.id,
    onClose,
    onRegenerated,
    regenerateAssetScopedShortId,
    shortIdInfo,
    trackLocation,
  ]);

  return (
    <Modal isOpen onDismiss={onClose} data-testid="REGENERATE_SHARE_LINK_MODAL">
      <ShareModalHeader onClose={onClose} title="Generate new link?" />
      <ModalDescription>
        This will break the current URL and people with the old link will no longer have access to the asset. Are you
        sure you want to generate a new URL?
      </ModalDescription>
      <RegenerateShareLinkButtons
        isLoading={regenerateAssetScopedShortId.isPending}
        onRegenerate={onRegenerate}
        onClose={() => {
          onClose();
          onCancel();
        }}
      />
    </Modal>
  );
};
